/**
 * Cloudflare Turnstile for the public forms (contact, business, interpreter
 * and AILA requests). The function they post to (contactFormSubmitted /
 * businessFormSubmitted) checks the token with Cloudflare and sends no email
 * without a valid one.
 *
 * Shared by the React forms and by the static /interpretation/<language> and
 * /aila pages, whose inline scripts copy turnstileGuard's own source
 * (tools/interpretation-shared.js, tools/seo-pages-aila.data.js). That is why
 * this file is CommonJS and plain ES5, and why turnstileGuard uses nothing
 * from outside itself.
 *
 * The widget is invisible unless Cloudflare wants the visitor to click it
 * ("interaction-only"), and Cloudflare's script is only loaded once someone
 * starts filling in the form, so pages that are only read don't load it.
 *
 * Each form uses its own action: contact, business, interpreter, aila. The
 * function accepts only the actions of its own forms.
 *
 * Forms also carry a hidden "website" field (HONEYPOT_FIELD) that people
 * never see; bots that fill in every field fill it in too, and the function
 * then sends nothing.
 */

/* eslint-disable no-var */

// The "eVerbary forms" widget (Cloudflare account Mailer.everbary@gmail.com,
// Turnstile, created 16-oct-2026, Managed). Its hostnames: everbary.com
// (covers www), localhost and the preview channel project-everbary--turnstile-rlkvn1cn.web.app.
var TURNSTILE_SITEKEY = '0x4AAAAAAFY-pLdgUf5GzFq8';
var HONEYPOT_FIELD = 'website';

/**
 * box: an empty element inside the form, where the widget shows when it
 * needs a click. Returns:
 *   start()  loads Cloudflare's script and runs the check (call it when the
 *            visitor starts on the form; calling it again does nothing)
 *   token()  a promise of the token, waiting for the check if it hasn't
 *            finished (up to 30 s; '' if there is none by then)
 *   reset()  after each send: a token works once, so this gets a new one
 *   remove() when the form goes away
 */
function turnstileGuard(box, action, sitekey) {
  var src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
  var widgetId = null;
  var current = '';
  var waiting = [];
  var removed = false;

  function settle(value) {
    current = value || '';
    var list = waiting;
    waiting = [];
    for (var i = 0; i < list.length; i++) list[i](current);
  }

  function load() {
    if (window.turnstile) return Promise.resolve(window.turnstile);
    if (!window.everbaryTurnstileLoad) {
      window.everbaryTurnstileLoad = new Promise(function (resolve, reject) {
        var s = document.createElement('script');
        s.src = src;
        s.async = true;
        s.onload = function () { resolve(window.turnstile); };
        s.onerror = function () { window.everbaryTurnstileLoad = null; reject(new Error('turnstile-load')); };
        document.head.appendChild(s);
      });
    }
    return window.everbaryTurnstileLoad;
  }

  function start() {
    if (widgetId !== null || removed) return;
    widgetId = 'loading';
    load().then(function (t) {
      if (removed || !box) return;
      widgetId = t.render(box, {
        sitekey: sitekey,
        action: action,
        appearance: 'interaction-only',
        callback: settle,
        'expired-callback': function () { current = ''; },
        'error-callback': function () { current = ''; }
      });
    }).catch(function () { widgetId = null; settle(''); });
  }

  function token() {
    start();
    if (current) return Promise.resolve(current);
    return new Promise(function (resolve) {
      waiting.push(resolve);
      setTimeout(function () { resolve(current); }, 30000);
    });
  }

  function reset() {
    current = '';
    if (window.turnstile && widgetId !== null && widgetId !== 'loading') window.turnstile.reset(widgetId);
  }

  function remove() {
    removed = true;
    if (window.turnstile && widgetId !== null && widgetId !== 'loading') window.turnstile.remove(widgetId);
    widgetId = null;
  }

  return { start: start, token: token, reset: reset, remove: remove };
}

module.exports = {
  TURNSTILE_SITEKEY: TURNSTILE_SITEKEY,
  HONEYPOT_FIELD: HONEYPOT_FIELD,
  turnstileGuard: turnstileGuard
};
